import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl, defineMessages } from 'react-intl';
import { rgba } from 'polished';
import classNames from 'classnames';

import RemoveDefaultButtonStyles from '../styles/RemoveDefaultButtonStyles';
import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import Typography from '../definitions/Typography';
import MessagePropType from '../definitions/MessagePropType';
import Caption from './Caption';
import IconChooser from './IconChooser';
import Dot from './Dot';


function Breadcrumb({
    message,
    descriptor,
    values,

    isActive = false,
    isCompleted = false,
    isDisabled = false,

    stepNumber,
    iconKey,

    onClick,
    'data-name': dataName,

    ...otherProps
}) {
    const intl = useIntl();

    const isBreadcrumbActive = typeof isActive === 'function' ? isActive() : isActive;
    const isBreadcrumbCompleted = typeof isCompleted === 'function' ? isCompleted() : isCompleted;
    const isBreadcrumbDisabled = typeof isDisabled === 'function' ? isDisabled() : isDisabled;

    return (
        <Container
            as={onClick ? 'button' : 'div'}
            data-name={onClick && dataName ? `breadcrumb-${dataName}` : undefined}
            className={classNames({
                'is-disabled': isBreadcrumbDisabled,
                'is-active': isBreadcrumbActive,
            })}

            isActive={isBreadcrumbActive}
            isCompleted={isBreadcrumbCompleted}
            isDisabled={isBreadcrumbDisabled}

            shouldDisplayUppercase={!!stepNumber}
            fontWeight={
                stepNumber || iconKey
                    ? Typography.WEIGHTS.MEDIUM
                    : Typography.WEIGHTS.REGULAR
            }

            onClick={onClick}
            /* Requirement: Cannot interact with active or disabled breadcrumb */
            disabled={onClick ? isBreadcrumbDisabled || isBreadcrumbActive : undefined} // Cannot use disabled on text

            {...otherProps}
        >
            <Choose>
                <When condition={iconKey}>
                    <IconChooser
                        className="icon"
                        iconKey={iconKey}

                        size="S"

                        stroke={isBreadcrumbDisabled ? Colours.STONE_DARK : Colours.BLACKBERRY}
                        fill={Colours.COCONUT}

                        strokeWidth="2"
                        outlineWidth="0"
                    />
                </When>
                <When condition={isBreadcrumbCompleted && !isBreadcrumbActive}>
                    <IconChooser
                        className="icon"
                        iconKey="checkmark"

                        size="XS"

                        stroke={stepNumber ? Colours.BLACKBERRY : Colours.MINT_DARK}
                        fill={stepNumber ? Colours.MINT_DARK : Colours.TRANSPARENT}

                        strokeWidth="4"
                        outlineWidth="0"

                        aria-label={intl.formatMessage(MESSAGES.COMPLETED)}
                    />
                </When>
                <When condition={stepNumber}>
                    <span className="step-number">
                        {stepNumber}
                    </span>
                </When>
                <Otherwise>
                    <Dot
                        className="dot"
                        backgroundColour={isBreadcrumbActive ? Colours.BLUEBERRY : Colours.TRANSPARENT}
                    />
                </Otherwise>
            </Choose>

            <Caption
                className="message"
                forwardedAs="span"
                message={message ? message : { descriptor, values }}
            />
        </Container>
    );
}

Breadcrumb.propTypes = {
    message: MessagePropType, // Currently non-required due to legacy descriptor & values
    descriptor: PropTypes.shape({
        id: PropTypes.string.isRequired,
        description: PropTypes.string,
        defaultMessage: PropTypes.string,
    }),
    values: PropTypes.object,

    isActive: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.func,
    ]),
    isCompleted: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.func,
    ]),
    isDisabled: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.func,
    ]),

    stepNumber: PropTypes.number,
    iconKey: PropTypes.string,

    onClick: PropTypes.func,
    'data-name': PropTypes.string,
};


const Container = styled.div`
    display: flex;
    align-items: center;

    margin: 0;
    padding: ${Sizes.SPACING.HALF};

    font-family: ${Typography.FONT_FAMILY_STACK};

    transition: color 300ms;

    .icon,
    .step-number,
    .dot {
        flex-shrink: 0;
        align-self: flex-start;
    }

    .step-number {
        display: flex;
        align-items: center;
        justify-content: center;

        margin-bottom: 0;
        margin-top: 1px;

        width: ${Sizes.ICONS.XS};
        height: ${Sizes.ICONS.XS};

        font-size: ${Sizes.FONTS['2XS']};
        font-weight: ${Typography.WEIGHTS.MEDIUM};

        border-radius: 50%;
        border: 1px solid;

        box-sizing: border-box;

        /* isActive */
        ${props => props.isActive && `
            color: ${Colours.COCONUT};
            border: 1px solid ${Colours.BLUEBERRY};
            background-color: ${Colours.BLUEBERRY};
        `}

        /* Visited */
        ${props => !props.isActive && !props.isCompleted && `
            color: ${Colours.BLACKBERRY};
            border: 1px solid ${Colours.BLACKBERRY};
        `}

        /* Disabled */
        ${props => props.isDisabled && `
            color: ${Colours.STONE_DARK};
            border: 1px solid ${Colours.STONE_DARK};
        `}
    }

    .dot {
        width: ${Sizes.SPACING.THREE_QUARTERS};
        height: ${Sizes.SPACING.THREE_QUARTERS};
        margin: 5px calc(${Sizes.SPACING.THREE_QUARTERS} / 2);
    }

    .message {
        margin: 0 0 0 ${Sizes.SPACING.THREE_QUARTERS};
        padding: 3px 0;

        font-size: ${Sizes.FONTS.XS};
        font-weight: ${props => props.fontWeight};
        text-align: left;

        color: inherit;

        /* Uppercase only when we have a step number */
        ${props => props.shouldDisplayUppercase && `
            text-transform: uppercase;
        `}

        /* Disabled */
        ${props => props.isDisabled && `
            color: ${Colours.STONE_DARK};
        `}
    }

    /* These styles apply when this Container element has changed to a button */
    button& {
        ${RemoveDefaultButtonStyles};
        padding: ${Sizes.SPACING.HALF};

        &.is-disabled {
            color: ${Colours.STONE_DARK};
        }

        &.is-active {
            color: ${Colours.BLACKBERRY};
        }

        .message {
            color: inherit;
            transition: color 300ms;
        }

        &:hover:enabled {
            cursor: pointer;

            .message {
                color: ${Colours.BLUEBERRY};
            }
        }

        &:focus:enabled {
            box-shadow: 0px 0px 2px 2px ${rgba(Colours.BLUEBERRY, 0.4)};
        }

        &:active:enabled {
            .message {
                color: ${Colours.BLUEBERRY_DARK};
            }
        }
    }
`;

const MESSAGES = defineMessages({
    COMPLETED: {
        id: 'base-ui.breadcrumb.completed',
        defaultMessage: 'Completed',
    },
});

export default Breadcrumb;
